<script context="module">
  import gql from 'graphql-tag';
  import { client } from 'utils/apollo.js';

  async function queryCollections(categoryId) {
    const res = await client.query({
      query: gql`
        query ProductCollectionsByCategory($_eq: Int!) {
          ProductCollections(where: { categoryId: { _eq: $_eq }, visible: { _eq: true } }, order_by: { name: asc }) {
            id
            name
            slug
          }
        }
      `,
      variables: {
        _eq: categoryId,
      },
    });
    return res.data.ProductCollections;
  }

  async function queryCategories() {
    const res = await client.query({
      query: gql`
        query GetVisibleProductCategories {
          ProductCategories(where: { visible: { _eq: true } }, order_by: { sortOrder: desc_nulls_last }) {
            id
            name
            slug
            sortOrder
            ProductCollections(order_by: { sortOrder: desc_nulls_last }, where: { visible: { _eq: true } }) {
              id
              slug
            }
          }
        }
      `,
    });
    return res.data.ProductCategories;
  }

  async function queryDefaultBulkDiscounts() {
    const res = await client.query({
      query: gql`
        query GetDefaultBulkDiscounts {
          BulkPricingDiscounts(where: { productId: { _is_null: true } }, order_by: { minQty: asc }) {
            id
            minQty
            maxQty
            discountPercent
          }
        }
      `,
    });
    return res.data.BulkPricingDiscounts;
  }

  export async function preload(page) {
    const productCategoriesRaw = await queryCategories();
    // Now we want to loop the categories and setup the slug for each category
    const productCategories = productCategoriesRaw.map((category) => {
      const collections = category.ProductCollections;
      const firstCollection = collections[0];
      const slug = firstCollection ? `${category.slug}/${firstCollection.slug}` : category.slug;
      return {
        id: category.id,
        name: category.name,
        slug,
      };
    });

    const defaultBulkDiscounts = await queryDefaultBulkDiscounts();

    const sportsCollections = await queryCollections(1);
    const workCollections = await queryCollections(2);
    const schoolCollections = await queryCollections(3);
    const eventCollections = await queryCollections(4);

    return {
      sportsCollections,
      workCollections,
      eventCollections,
      schoolCollections,
      productCategories,
      defaultBulkDiscounts,
    };
  }
</script>

<script>
  import { onMount, setContext } from 'svelte';
  import { stores } from '@sapper/app';

  import { environment } from '../environment/environment.js';

  import Footer from '../components/Footer.svelte';
  import Nav from '../components/Nav/Nav.svelte';
  import StickyPromoBar from '../layouts/home/StickyPromoBar.svelte';
  import Toast from '../components/Toast.svelte';

  import { webPSupport } from '../stores/WebP.js';
  import { isMobile } from '../stores/IsMobile.js';
  import { cssTransformSupport } from '../stores/CssTransform.js';
  import { cartDetails, shoppingCartId } from '../stores/Cart.js';
  import { showQuoteForm } from '../stores/ShowQuoteForm.js';
  import QuoteModal from '../components/QuoteModal.svelte';

  export let segment;
  export let productCategories;
  export let sportsCollections;
  export let workCollections;
  export let schoolCollections;
  export let eventCollections;
  export let defaultBulkDiscounts;

  const { page } = stores();

  // Add default bulk discounts to context for access throughout app
  setContext('defaultBulkDiscounts', defaultBulkDiscounts);

  function determineTrackingDeviceType(deviceType) {
    if (deviceType === 'c') {
      return 'desktop';
    }
    if (deviceType === 'm') {
      return 'mobile';
    }
    if (deviceType === 't') {
      return 'tablet';
    }

    return 'unknown';
  }

  function determineTrackingNetworkType(networkType) {
    if (networkType === 'g') {
      return 'google';
    }
    if (networkType === 's') {
      return 'search partner';
    }
    if (networkType === 'd') {
      return 'display network';
    }
    if (networkType === 'u') {
      return 'smart shopping';
    }
    if (networkType === 'ytv') {
      return 'youtube video';
    }
    if (networkType === 'vp') {
      return 'video partner';
    }

    return 'unknown';
  }

  function determineTrackingMatchType(matchType) {
    if (matchType === 'e') {
      return 'exact';
    }
    if (matchType === 'p') {
      return 'phrase';
    }
    if (matchType === 'b') {
      return 'broad';
    }
    if (matchType === 'm') {
      return 'modified broad';
    }

    return 'unknown';
  }

  function determineTrackingTypeProperty(trackingParamsArray, propertyString) {
    const property = trackingParamsArray.find((param) => param.includes(propertyString));
    if (property) {
      return property.replace(`${propertyString}-`, '');
    }

    return undefined;
  }

  // Record google ad tracking params
  const adTrackingParams = $page.query['ad-tracking']?.split('_');

  let adTracking = null;
  if (adTrackingParams) {
    adTracking = {
      device: determineTrackingDeviceType(determineTrackingTypeProperty(adTrackingParams, 'dev')),
      network: determineTrackingNetworkType(determineTrackingTypeProperty(adTrackingParams, 'net')),
      matchType: determineTrackingMatchType(determineTrackingTypeProperty(adTrackingParams, 'match')),
      keyword: determineTrackingTypeProperty(adTrackingParams, 'kw'),
      campaignId: determineTrackingTypeProperty(adTrackingParams, 'cid'),
      adGroupId: determineTrackingTypeProperty(adTrackingParams, 'agi'),
      placement: determineTrackingTypeProperty(adTrackingParams, 'place'),
      adGroup: determineTrackingTypeProperty(adTrackingParams, 'group'),
      campaignName: determineTrackingTypeProperty(adTrackingParams, 'camp'),
      platform: determineTrackingTypeProperty(adTrackingParams, 'plat'),
      productId: determineTrackingTypeProperty(adTrackingParams, 'pid'),
      adName: determineTrackingTypeProperty(adTrackingParams, 'adn'),
      siteSourceName: determineTrackingTypeProperty(adTrackingParams, 'ssn'),
    };
  }

  // Check WebP support
  // https://stackoverflow.com/questions/5573096/detecting-webp-support
  function support_format_webp() {
    var elem = document.createElement('canvas');
    elem.width = 1;
    elem.height = 1;

    if (!!(elem.getContext && elem.getContext('2d'))) {
      // was able or not to get WebP representation
      return elem.toDataURL('image/webp').indexOf('data:image/webp') == 0;
    } else {
      // very old browser like IE 8, canvas not supported
      return false;
    }
  }

  if (typeof window !== 'undefined') {
    $webPSupport = support_format_webp();
  }

  function determineIsMobile() {
    if (typeof window !== 'undefined') {
      return window.innerWidth < 800;
    }

    return true;
  }

  $isMobile = determineIsMobile();

  function resize() {
    $isMobile = determineIsMobile();
  }

  //Check if CSS transforms are available
  //https://stackoverflow.com/questions/7212102/detect-with-javascript-or-jquery-if-css-transform-2d-is-available
  function supportCssTransform() {
    //let prefixes = 'transform WebkitTransform MozTransform OTransform msTransform'.split(' ');
    //TODO - add all the css prefixes as fallback options in banner rotation
    let prefixes = 'transform';
    let div = document.createElement('div');
    for (let i = 0; i < prefixes.length; i++) {
      if (div && div.style[prefixes[i]] !== undefined) {
        return true;
      }
    }
    return false;
  }

  if (typeof window !== 'undefined') {
    $cssTransformSupport = supportCssTransform();
  }

  onMount(async () => {
    if ($shoppingCartId) {
      const status = await cartDetails.loadCart($shoppingCartId);

      if (status === 'error') {
        cartDetails.createShoppingCart();
      }
    } else {
      await cartDetails.createShoppingCart();
    }

    // adtracking table - cartId, device, network, matchType, keyword, campaignId, adGroupId, placement, adGroup, createdAt
    if (adTracking) {
      cartDetails.saveAdTrackingData($shoppingCartId, adTracking);
    }
  });
</script>

<svelte:window on:resize={resize} />

<QuoteModal bind:show={$showQuoteForm} />

<Toast />
{#if segment !== 'checkout' && segment !== 'design' && segment !== 'custom'}
  {#if !segment && environment.SHOW_CHRISTMAS_MESSAGE}
    <StickyPromoBar offerText="Orders Placed After Dec 10th Won't Ship Until Mid January" />
  {/if}
  <Nav {segment} {productCategories} {sportsCollections} {workCollections} {eventCollections} {schoolCollections} />
{/if}

<main>
  <slot />
</main>

{#if segment !== 'checkout' && segment !== 'design' && segment !== 'custom'}
  <Footer />
{/if}

<style>
</style>
